import {
	CloseCircleOutlined,
	FileTextOutlined,
	InfoCircleOutlined,
	LeftOutlined,
	UnorderedListOutlined,
	WalletOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Avatar, Button, Card, Input, Modal, message, Table, Tag, Timeline } from "antd";
import { useState } from "react";
import { useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import type { AppRole } from "@/shared/config/navigation";
import { canCancelSale as canCancelSaleByRole } from "@/shared/config/permissions";
import { parseApiError } from "@/shared/utils/error";
import { formatMoney } from "@/shared/utils/money";
import { getSaleById, submitSaleCancel } from "../services/sales.backend";

const { TextArea } = Input;

type SaleDetailLocationState = {
	fromDashboard?: boolean;
	returnTo?: string;
};

export function SaleDetailPage() {
	const navigate = useNavigate();
	const location = useLocation();
	const { saleId } = useParams();
	const { role } = useOutletContext<{ role: AppRole }>();
	const queryClient = useQueryClient();
	const canCancelSale = canCancelSaleByRole(role);
	const locationState = location.state as SaleDetailLocationState | null;

	const fromDashboard = locationState?.fromDashboard === true;
	const returnTo = locationState?.returnTo;

	const [cancelModalVisible, setCancelModalVisible] = useState(false);
	const [cancelReason, setCancelReason] = useState("");

	const { data: sale, isLoading } = useQuery({
		queryKey: ["sale", saleId],
		queryFn: () => {
			if (!saleId) {
				throw new Error("Sale ID is required");
			}
			return getSaleById(saleId);
		},
		enabled: !!saleId,
	});

	const cancelMutation = useMutation({
		mutationFn: submitSaleCancel,
		onSuccess: () => {
			message.success("Sotuv muvaffaqiyatli bekor qilindi, xona sotuvga qaytarildi.");
			setCancelModalVisible(false);
			queryClient.invalidateQueries({ queryKey: ["sale", saleId] });
			queryClient.invalidateQueries({ queryKey: ["sales"] });
			queryClient.invalidateQueries({ queryKey: ["rooms"] });
			if (sale?.roomId) {
				queryClient.invalidateQueries({ queryKey: ["room", sale.roomId] });
			}
		},
		onError: (error) => {
			message.error(parseApiError(error) || "Bekor qilishda xatolik yuz berdi");
		},
	});

	const handleBack = () => {
		if (returnTo) {
			navigate(returnTo);
		} else if (fromDashboard) {
			navigate(-1);
		} else {
			navigate("/sales");
		}
	};

	if (isLoading) {
		return <div className="p-8 text-center text-gray-500">Yuklanmoqda...</div>;
	}

	if (!sale) {
		return (
			<div className="p-8 text-center text-gray-500">
				Shartnoma topilmadi! <Button onClick={() => navigate("/sales")}>Ro'yxatga qaytish</Button>
			</div>
		);
	}

	const handleCancelSaleSubmit = async () => {
		if (!canCancelSale) {
			message.warning("Faqat Org Admin sotuvni bekor qila oladi.");
			return;
		}

		if (!cancelReason.trim()) {
			message.warning("Iltimos, bekor qilish sababini yozing!");
			return;
		}
		cancelMutation.mutate({ reason: cancelReason, saleId: sale.id });
	};

	// Determine KPIs
	const remainingAmount = Math.max(0, sale.finalPrice - sale.paidAmount);
	const overdueDebt = sale.schedule
		.filter((s) => s.status === "delayed")
		.reduce((sum, s) => sum + (s.plannedAmount - s.paidAmount), 0);

	// Short schedule (first 3 rows)
	const shortSchedule = sale.schedule.slice(0, 3);

	const scheduleColumns = [
		{
			title: "#",
			dataIndex: "index",
			key: "index",
			width: 60,
			render: (val: number) => <span className="font-bold">{val}</span>,
		},
		{
			title: "Sana",
			dataIndex: "date",
			key: "date",
		},
		{
			title: "Reja Summa",
			dataIndex: "plannedAmount",
			key: "plannedAmount",
			render: (val: number) => formatMoney(val),
		},
		{
			title: "To'langan",
			dataIndex: "paidAmount",
			key: "paidAmount",
			render: (val: number) => formatMoney(val),
		},
		{
			title: "To'langan sana",
			dataIndex: "paidAt",
			key: "paidAt",
			render: (val: string | undefined) => val || "-",
		},
		{
			title: "Holat",
			dataIndex: "status",
			key: "status",
			render: (status: string) => {
				let color = "default";
				let text = "Rejada";
				if (status === "paid") {
					color = "success";
					text = "To'landi";
				} else if (status === "expected") {
					color = "warning";
					text = "Kutilmoqda";
				} else if (status === "delayed") {
					color = "error";
					text = "Kechikkan";
				}
				return <Tag color={color}>{text}</Tag>;
			},
		},
	];

	return (
		<div className="crm-sale-detail">
			<div className="crm-page-head">
				<div>
					<Button className="crm-back-link" icon={<LeftOutlined />} onClick={handleBack}>
						{returnTo === "/payments"
							? "To'lovlarga qaytish"
							: fromDashboard
								? "Bosh sahifaga qaytish"
								: "Sotuvlar"}
					</Button>
					<h1 style={{ marginTop: "4px" }}>Sotuv #{sale.saleNumber}</h1>
					<p style={{ color: "var(--crm-ink-3)" }}>
						{fromDashboard ? (
							<span style={{ color: "var(--crm-text-secondary)" }}>Bosh sahifa / </span>
						) : null}
						Yaratildi: {sale.createdAt} - Menejer: {sale.managerName}
					</p>
				</div>
				<div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
					{sale.status === "faol" && (
						<Tag color="blue" className="text-sm px-3 py-1 font-bold">
							Faol
						</Tag>
					)}
					{sale.status === "yakunlangan" && (
						<Tag color="green" className="text-sm px-3 py-1 font-bold">
							Yakunlangan
						</Tag>
					)}
					{sale.status === "bekor" && (
						<Tag color="red" className="text-sm px-3 py-1 font-bold">
							Bekor qilingan
						</Tag>
					)}

					<Button
						icon={<FileTextOutlined />}
						onClick={() => navigate(`/contract?saleId=${sale.id}`, { state: location.state })}
					>
						Shartnoma
					</Button>
					{sale.status === "faol" && (
						<>
							<Button
								className="crm-primary-btn"
								type="primary"
								icon={<WalletOutlined />}
								onClick={() =>
									navigate(`/payments/new?saleId=${sale.id}`, { state: location.state })
								}
							>
								To'lov
							</Button>
							{canCancelSale ? (
								<Button
									danger
									icon={<CloseCircleOutlined />}
									onClick={() => setCancelModalVisible(true)}
								>
									Bekor qilish
								</Button>
							) : null}
						</>
					)}
				</div>
				<div className="crm-head-actions" style={{ marginLeft: "auto" }}>
					<Button icon={<UnorderedListOutlined />} onClick={() => navigate("/sales")}>
						Ro'yxat
					</Button>
				</div>
			</div>

			{/* KPI Section */}
			<div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
				<Card className="crm-card border-l-4 border-emerald-500">
					<div className="text-gray-400 text-xs">To'langan summa</div>
					<div className="text-2xl font-black text-emerald-600 mt-1">
						{formatMoney(sale.paidAmount)} UZS
					</div>
					<div className="text-xs text-gray-400 mt-1">Jami summadan {sale.paidPercent}%</div>
				</Card>

				<Card className="crm-card border-l-4 border-blue-500">
					<div className="text-gray-400 text-xs">Qoldiq qarz</div>
					<div className="text-2xl font-black text-blue-600 mt-1">
						{formatMoney(remainingAmount)} UZS
					</div>
					<div className="text-xs text-gray-400 mt-1">
						{Math.round((100 - sale.paidPercent) * 100) / 100}% qoldi
					</div>
				</Card>

				<Card className="crm-card border-l-4 border-red-500">
					<div className="text-gray-400 text-xs">Kechikkan qarz</div>
					<div className="text-2xl font-black text-red-600 mt-1">
						{formatMoney(overdueDebt)} UZS
					</div>
					<div className="text-xs text-gray-400 mt-1">Bugungi kunga qarz</div>
				</Card>

				<Card className="crm-card border-l-4 border-purple-500">
					<div className="text-gray-400 text-xs">Oylik to'lov</div>
					<div className="text-2xl font-black text-purple-600 mt-1">
						{formatMoney(sale.monthlyPayment)} UZS
					</div>
					<div className="text-xs text-gray-400 mt-1">Har oy grafik sanasigacha</div>
				</Card>
			</div>

			{/* Main Grid: Details columns */}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
				<Card title="Mijoz" className="crm-card">
					<div className="flex items-center gap-3 mb-4">
						<Avatar
							size={48}
							style={{ backgroundColor: "#2756d8", fontSize: "18px", fontWeight: "bold" }}
						>
							{sale.clientName.substring(0, 2).toUpperCase()}
						</Avatar>
						<div>
							<h4
								className="font-bold text-base text-gray-900"
								style={{ margin: 0, lineHeight: 1.2 }}
							>
								{sale.clientName}
							</h4>
							<span className="text-xs text-gray-500">{sale.phone}</span>
						</div>
					</div>
					<div className="text-xs text-gray-500 space-y-1">
						<div>- Tizimdagi maqomi: Shartnoma egasi</div>
						<div>- Xabarlar kanali: Avtomatik SMS yoqilgan</div>
					</div>
				</Card>

				<Card title="Obyekt (Kvartira)" className="crm-card">
					<div className="space-y-2 text-sm text-gray-700">
						<div className="flex justify-between">
							<span className="text-gray-500">Bino nomi:</span>
							<span className="font-semibold">{sale.buildingName}</span>
						</div>
						<div className="flex justify-between">
							<span className="text-gray-500">Blok / Qavat:</span>
							<span className="font-semibold">
								{sale.blockName} - {sale.floor}-qavat
							</span>
						</div>
						<div className="flex justify-between">
							<span className="text-gray-500 font-semibold text-blue-600">Xona raqami:</span>
							<span className="font-bold text-blue-600">{sale.roomNumber}-xona</span>
						</div>
						<div className="flex justify-between">
							<span className="text-gray-500">Umumiy maydon:</span>
							<span className="font-semibold">{sale.area} m2</span>
						</div>
					</div>
				</Card>

				<Card title="Moliyaviy ko'rsatkichlar" className="crm-card">
					<div className="space-y-2 text-sm text-gray-700">
						<div className="flex justify-between">
							<span className="text-gray-500">Boshlang'ich narx:</span>
							<span>{formatMoney(sale.basePrice)} UZS</span>
						</div>
						<div className="flex justify-between font-bold border-t pt-2 border-gray-200">
							<span className="text-gray-600">Yakuniy narx:</span>
							<span className="text-blue-600">{formatMoney(sale.finalPrice)} UZS</span>
						</div>
						<div className="flex justify-between text-xs text-gray-500 pt-1">
							<span>To'lov shakli:</span>
							<span className="capitalize">
								{sale.paymentType === "muddatli"
									? `Bo'lib to'lash (${sale.monthsCount} oy)`
									: "Naqd / To'liq"}
							</span>
						</div>
					</div>
				</Card>
			</div>

			{/* Bottom Grid: Short schedule & Audit trail */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				<Card
					title="To'lov grafigi (Qisqacha)"
					className="crm-card"
					extra={
						<Button
							type="link"
							onClick={() => navigate(`/sales/${sale.id}/schedule`, { state: location.state })}
							className="p-0 font-semibold"
						>
							To'liq grafikni ko'rish
						</Button>
					}
				>
					<Table
						dataSource={shortSchedule}
						columns={scheduleColumns}
						rowKey="index"
						pagination={false}
						size="small"
					/>
				</Card>

				<Card title="Audit jurnali (Tarix)" className="crm-card">
					<Timeline
						items={sale.auditTrail.map((log) => ({
							color:
								log.action === "sale.cancelled"
									? "red"
									: log.action === "payment.received"
										? "green"
										: "blue",
							children: (
								<div>
									<div className="font-semibold text-xs text-gray-900">
										{log.action} - {log.date}
									</div>
									<div className="text-xs text-gray-500 mt-0.5">
										{log.details} ({log.user})
									</div>
								</div>
							),
						}))}
					/>
				</Card>
			</div>

			{/* Cancel Sale Modal */}
			<Modal
				title="Shartnomani bekor qilish"
				open={cancelModalVisible}
				onOk={handleCancelSaleSubmit}
				onCancel={() => setCancelModalVisible(false)}
				okText="Shartnomani bekor qilish"
				cancelText="Yopish"
			>
				<div className="space-y-4 pt-2">
					<div className="bg-amber-50 border border-amber-200 p-3 rounded text-amber-800 text-xs flex gap-2">
						<InfoCircleOutlined className="text-sm mt-0.5" />
						<span>
							Diqqat: Shartnomani bekor qilganingizda xona holati avtomatik ravishda <b>SOTUVDA</b>{" "}
							(bo'sh) holatiga o'tadi va barcha keyingi to'lovlar bekor qilinadi. Ushbu amalni
							qaytarib bo'lmaydi!
						</span>
					</div>
					<div>
						<div className="block text-xs font-semibold mb-1">Bekor qilish sababi</div>
						<TextArea
							rows={4}
							value={cancelReason}
							onChange={(e) => setCancelReason(e.target.value)}
							placeholder="Mijoz to'lov layoqatsizligi sababli rad etdi..."
						/>
					</div>
				</div>
			</Modal>
		</div>
	);
}

export default SaleDetailPage;
